import clsx from 'clsx';
import { useCallback, useLayoutEffect, useRef } from 'react';

import { Field, fieldStyles, getFieldIconColor, useFieldId } from '../Field';
import { Typography } from '../typography';

import type { FieldSize, FieldVariant } from '../Field';
import type { ChangeEvent, DetailedHTMLProps, ReactNode, TextareaHTMLAttributes } from 'react';

import s from './TextareaField.module.css';

type TextareaFieldSize = FieldSize;
type TextareaFieldVariant = FieldVariant;

type TextareaFieldProps = Omit<
  DetailedHTMLProps<TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>,
  'prefix' | 'size'
> & {
  /** The element is displayed at the bottom end */
  bottomSuffix?: ReactNode;
  /** Container className */
  containerClassName?: string;
  /** Make textarea take full width of its container */
  fullWidth?: boolean;
  /** Helper text displayed below the textarea */
  hint?: ReactNode;
  /** Label text displayed above the textarea */
  label?: string;
  /** Maximum number of rows when auto-sizing */
  maxRows?: number;
  /** Minimum number of rows when auto-sizing */
  minRows?: number;
  /** Icon or element displayed at the start of the label row */
  prefix?: ReactNode;
  /** Size of the textarea field
   * @default 'default'
   */
  size?: TextareaFieldSize;
  /** Element displayed at the end of the label row */
  suffix?: ReactNode;
  /** Textarea className */
  textareaClassName?: string;
  /** Visual state of the textarea
   * @default 'default'
   */
  variant?: TextareaFieldVariant;
};

const parseCssSize = (value: string) => Number.parseFloat(value) ?? 0;

const getLineHeight = (style: CSSStyleDeclaration) => {
  const lineHeight = Number.parseFloat(style.lineHeight);

  if (Number.isFinite(lineHeight)) {
    return lineHeight;
  }

  const fontSize = parseCssSize(style.fontSize);

  if (Number.isNaN(fontSize)) {
    return 0;
  }

  if (fontSize === 0) {
    return 0;
  }

  return fontSize * 1.2;
};

const AUTOSIZE_DEFAULT_MIN_ROWS = 3;

export const TextareaField = ({
  bottomSuffix,
  className,
  containerClassName,
  disabled,
  fullWidth,
  hint,
  id,
  label,
  maxRows,
  minRows,
  onChange,
  placeholder,
  prefix,
  ref,
  required,
  size = 'default',
  suffix,
  textareaClassName,
  value,
  variant = 'default',
  ...props
}: TextareaFieldProps) => {
  const textareaId = useFieldId(id);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const labelContainerRef = useRef<HTMLDivElement | null>(null);
  const bottomContainerRef = useRef<HTMLDivElement | null>(null);

  const hasLabel = Boolean(label);
  const hasPrefix = Boolean(prefix);
  const hasSuffix = Boolean(suffix);
  const hasBottomContainer = Boolean(bottomSuffix);
  const hasBottomSuffix = Boolean(bottomSuffix);
  const shouldAutosize = minRows != null || maxRows != null;
  const resolvedMinRows = minRows ?? AUTOSIZE_DEFAULT_MIN_ROWS;

  const iconColor = getFieldIconColor(variant, disabled);
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  const hintId = hint ? `${textareaId}-hint` : undefined;
  const labelId = label ? `${textareaId}-label` : undefined;

  const syncTextareaHeight = useCallback(() => {
    const wrapper = wrapperRef.current;
    const textarea = textareaRef.current;

    if (!wrapper || !textarea || !shouldAutosize) {
      return;
    }

    wrapper.style.height = 'auto';
    textarea.style.height = 'auto';

    const style = window.getComputedStyle(textarea);
    const lineHeight = getLineHeight(style);

    const verticalPadding = parseCssSize(style.paddingTop) + parseCssSize(style.paddingBottom);
    const verticalBorder = parseCssSize(style.borderTopWidth) + parseCssSize(style.borderBottomWidth);

    const minHeight = lineHeight > 0 ? lineHeight * resolvedMinRows + verticalPadding : 0;
    const maxHeight =
      maxRows != null && lineHeight > 0 ? lineHeight * maxRows + verticalPadding : Number.POSITIVE_INFINITY;

    const contentHeight = textarea.scrollHeight;

    const nextHeight = Math.max(minHeight, Math.min(contentHeight, maxHeight));

    const finalTextareaHeight = style.boxSizing === 'border-box' ? nextHeight + verticalBorder : nextHeight;
    const labelHeight = labelContainerRef.current?.offsetHeight ?? 0;
    const bottomHeight = bottomContainerRef.current?.offsetHeight ?? 0;
    const wrapperStyle = window.getComputedStyle(wrapper);
    const wrapperVerticalPadding = parseCssSize(wrapperStyle.paddingTop) + parseCssSize(wrapperStyle.paddingBottom);

    const finalWrapperHeight = labelHeight + finalTextareaHeight + bottomHeight + wrapperVerticalPadding;

    wrapper.style.height = `${finalWrapperHeight}px`;

    textarea.style.overflowY = maxRows != null && contentHeight > maxHeight ? 'auto' : 'hidden';
  }, [maxRows, resolvedMinRows, shouldAutosize, bottomContainerRef]);

  const setTextareaRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      textareaRef.current = node;

      if (typeof ref === 'function') {
        ref(node);
      } else if (ref != null && typeof ref === 'object') {
        (ref as { current: HTMLTextAreaElement | null }).current = node;
      }
    },
    [ref],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      syncTextareaHeight();
      onChange?.(event);
    },
    [onChange, syncTextareaHeight],
  );

  useLayoutEffect(() => {
    if (!shouldAutosize) {
      const wrapper = wrapperRef.current;
      const textarea = textareaRef.current;

      if (wrapper) {
        wrapper.style.height = '';
      }

      if (textarea) {
        textarea.style.height = '';
        textarea.style.overflowY = '';
      }

      return undefined;
    }

    syncTextareaHeight();

    const handleResize = () => {
      syncTextareaHeight();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [shouldAutosize, syncTextareaHeight, value, placeholder, suffix, prefix, label, bottomSuffix]);

  return (
    <Field className={containerClassName} disabled={disabled} hint={hint} hintId={hintId} variant={variant}>
      <Field.Wrapper
        hasPrefix={hasPrefix}
        ref={wrapperRef}
        className={s.textareaWrapper}
        disabled={disabled}
        fullWidth={fullWidth}
        hasSuffix={hasSuffix}
        size={size}
        variant={variant}
      >
        {hasLabel && (
          <div ref={labelContainerRef} className={s.labelContainer}>
            {hasPrefix && <Field.Prefix color={iconColor}>{prefix}</Field.Prefix>}

            <label className={s.label} htmlFor={textareaId} id={labelId}>
              <Typography color={iconColor} component="span" variant="inset-paragraph">
                {label}
              </Typography>
            </label>

            {hasSuffix && <Field.Suffix color={iconColor}>{suffix}</Field.Suffix>}
          </div>
        )}

        <textarea
          ref={setTextareaRef}
          className={clsx(
            s.textarea,
            !shouldAutosize && s.textareaStaticHeight,
            fieldStyles.control,
            fieldStyles[`control--variant-${variant}`],
            textareaClassName,
          )}
          aria-describedby={hintId}
          aria-invalid={variant === 'danger'}
          aria-labelledby={hasLabel ? labelId : undefined}
          aria-required={required}
          disabled={disabled}
          id={textareaId}
          onChange={handleChange}
          placeholder={placeholder}
          required={required}
          rows={shouldAutosize ? resolvedMinRows : props.rows}
          value={value}
          {...props}
        />

        {hasBottomContainer && (
          <div ref={bottomContainerRef} className={s.bottomContainer}>
            {hasBottomSuffix && <div className={s.bottomSuffix}>{bottomSuffix}</div>}
          </div>
        )}
      </Field.Wrapper>
    </Field>
  );
};
